<template>
    <page-shell title="Tools">
        <template v-if="isManagementUser">
            <h2>Downloads</h2>
            <div class="download-actions">
                <p>Download all customer data as CSV</p>
                <el-button @click="downloadCSV">Download</el-button>
                <p>Download invoice data per month</p>
                <v-select
                    v-model="selectedMonthlyPeriod"
                    :options="invoicePeriodsMonthly"
                    :reduce="optionId"
                    label="label"
                    :clearable="false"
                    placeholder="Select month"
                />
                <el-button @click="downloadMonthlyInvoicingCSV">Download</el-button>
                <p>Download invoice data per quarter</p>
                <v-select
                    v-model="selectedQuarterlyPeriod"
                    :options="invoicePeriodsQuarterly"
                    :reduce="optionId"
                    label="label"
                    :clearable="false"
                    placeholder="Select quarter"
                />
                <el-button @click="downloadQuarterlyInvoicingCSV">Download</el-button>
            </div>
            <h2>Login</h2>
            <div class="login-actions">
                <p>Get mail with login link on behalf of a user</p>
                <el-input v-model="userEmail" placeholder="User email" />
                <el-button @click="sendLoginLink">Send</el-button>
            </div>
        </template>
        <template v-else>
            <p>Unauthorized access</p>
        </template>
    </page-shell>
</template>


<script setup>
import { computed, ref } from "vue";
import { isManagement } from "../../helpers/auth";
import { saveTextAsFile } from "../../helpers/saveTextasFile";
import useCurrentUserStore from "../../store/userStore";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { getInvoicingCSV, getCustomersCSVJSON, requestLoginLinkOnServer } from "../../api/rest";
import { Message } from "element-ui";
import PageShell from "../shared/PageShell.vue";

dayjs.extend(quarterOfYear)
const store = useCurrentUserStore();

const selectedMonthlyPeriod = ref(null);
const selectedQuarterlyPeriod = ref(null);
const userEmail = ref("");

const optionId = (item) => item.id;

const downloadCSV = async () => {
    const result = await getCustomersCSVJSON();
    saveTextAsFile(result.csvData, "customer_data.csv", "csv");
};


const downloadMonthlyInvoicingCSV = async () => {
    if (!selectedMonthlyPeriod.value) {
        alert("Please select a period first");
        return;
    }
    const period = invoicePeriodsMonthly.value.find(p => p.id === selectedMonthlyPeriod.value);
    if (!period) {
        alert("Invalid period selected");
        return;
    }
    const result = await getInvoicingCSV(period.year, period.month);
    saveTextAsFile(result, `invoicing_data_${period.label.toLowerCase().replace(" ", "_")}.csv`, "csv");
};

const downloadQuarterlyInvoicingCSV = async () => {
    if (!selectedQuarterlyPeriod.value) {
        alert("Please select a period first");
        return;
    }
    const period = invoicePeriodsQuarterly.value.find(p => p.id === selectedQuarterlyPeriod.value);
    if (!period) {
        alert("Invalid period selected");
        return;
    }
    const result = await getInvoicingCSV(period.year, period.quarter);
    saveTextAsFile(result, `invoicing_data_${period.label.toLowerCase().replace(" ", "_")}.csv`, "csv");
};


const isManagementUser = computed(() => {
    return isManagement(store.currentUser);
});


const invoicePeriodsMonthly = computed(() => {
    const periods = [];
    for (let i = 1; i < 12; i++) {
        const month = dayjs().subtract(i, "month");
        periods.push({ id: i, label: month.format("MMMM YYYY"), year: month.year(), month: month.month() + 1 });
    }
    return periods;
});

const invoicePeriodsQuarterly = computed(() => {
    const periods = [];
    const currentQuarter = dayjs().quarter();
    for (let i = 1; i < 4; i++) {
        const quarter = dayjs().subtract(i , "quarter");
        periods.push({ id: i, label: `Q${quarter.quarter()} ${quarter.year()}`, year: quarter.year(), quarter: quarter.quarter() });
    }
    return periods;
});

const sendLoginLink = async () => {
    if (!userEmail.value) {
        alert("Please enter a user email");
        return;
    }
    try {
        await requestLoginLinkOnServer(userEmail.value);
        Message.success({
            message: "Login link will be sent if email exists",
        });
    } catch (error) {
        Message.error({
            message: `Error from server : ${error.message}`,
        });
        console.error("Error sending login link:", error);
    }
};

</script>
<style scoped lang="scss">
.download-actions,
.login-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    max-width: 24rem;
}

h2 {
    margin: 1.5rem 0 0.75rem;
}
</style>